import axios from "axios";


export enum TodoState {
    DONE = "DONE",
    OPEN = "OPEN",
    WAITING="WAITING"
}

export enum TodoType {
    PROJECT = "PROJECT",
    FLAME = "FLAME"
}

export interface TodoSearchRequest {
    searchTerm?: string;
    states: TodoState[],
    types: TodoType[],
    page: number;
    pageSize: number;
    sortBy: string;
    sortDesc: boolean;
    start?: Date;
    ref?: string;
    showAll?: boolean;
}

export interface TodoSearchResult {
    totalPages: number;
    totalElements: number;
    page: number;
    pageSize: number;
    todos: Todo[];
}

export interface Todo {
    id: string | null;
    assigneeRef: string | null;
    title: string;
    feedback: string;
    ref: string;
    activities: string;
    start: Date;
    finished: Date | null;
    state: TodoState;
    type: TodoType;
    orderKey: number;
    createdBy?: string | null;
    assigneeStateColor?: string;
    capacityFurtherOpenTodoCounter: number | null;
    ownerRef?: string | null;
    parentTitle?: string | null;
    version: number
}

interface TodoApi {
    search(searchRequest: TodoSearchRequest): Promise<TodoSearchResult>;

    listByRef(ref: string | null): Promise<Todo[]>;

    mapDatesForTodo(todo: Todo): Todo;

    save(todos: Todo[]): Promise<Todo[]>;

    edit(todo: Todo): Promise<Todo>;

    add(todo: Todo): Promise<Todo[]>;

    delete(todo: Todo): Promise<void>;

    getById(ref: string): Promise<Todo | null>;

    addTodosByPackage(parentRef: string, todoPackageRef: string): Promise<Todo[]>;
}

export const todoApi: TodoApi = {
    async search(searchRequest) {
        const uri = `/api/todos/_search`;
        const result = (await axios.post(uri, searchRequest)).data;
        if (result) {
            result.todos = result.todos.map((v: Todo) => this.mapDatesForTodo(v));
            return result;
        }
        return null;
    },

    async save(todos) {
        const uri = `/api/todos/batch`;
        const result = (await axios.post(uri, todos)).data;
        if (result) {
            return result.map((v: Todo) => this.mapDatesForTodo(v));
        }
        return null;
    },

    async add(todo) {
        const uri = `/api/todos`;
        const result = (await axios.post(uri, todo)).data;
        if (result) {
            return result.map((v: Todo) => this.mapDatesForTodo(v));
        }
        return null;
    },

    async edit(todo) {
        const uri = `/api/todos/${todo.id}`;
        const result = (await axios.put(uri, todo)).data;
        return this.mapDatesForTodo(result);
    },


    async delete(todo) {
        const uri = `/api/todos/${todo.id}`;
        await axios.delete(uri);
    },

    async listByRef(ref) {
        if( ref == null) {
            return [];
        }
        const uri = `/api/todos/refs/${ref}`;
        const result = (await axios.get(uri)).data;
        if (result) {
            return result.map((v: Todo) => this.mapDatesForTodo(v));
        }
        return null;
    },

    async getById(ref) {
        const uri = `/api/todos/${ref}`;
        const result = (await axios.get(uri)).data;
        if (result) {
            return this.mapDatesForTodo(result);
        }
        return null;
    },

    async addTodosByPackage(parentRef, packageRef) {
        const uri = `/api/todos/refs/${parentRef}/todo-packages/${packageRef}`;
        const result = (await axios.put(uri)).data;
        if (result) {
            return result.map((v: Todo) => this.mapDatesForTodo(v));
        }
        return null;
    },

    mapDatesForTodo(todo: Todo): Todo {
        return {
            ...todo,
            start: new Date(todo.start),
            finished: todo.finished ? new Date(todo.finished) : null,
        } as Todo;

    }
}